(() => {
  const VERTEX_SHADER = `#version 300 es
    in vec2 aPosition;

    void main() {
      gl_Position = vec4(aPosition, 0.0, 1.0);
    }
  `;

  const REAL_FRAGMENT_SHADER = `#version 300 es
    precision highp float;

    out vec4 outColor;

    uniform vec2 uViewport;
    uniform float uDpr;
    uniform float uTime;
    uniform vec2 uPointer;
    uniform vec2 uVelocity;
    uniform vec4 uRipple;
    uniform vec4 uForm;
    uniform vec4 uField;
    uniform vec4 uAction;
    uniform vec4 uSocial[6];
    uniform float uSocialOpen;
    uniform float uFormFocus;
    uniform float uActionHover;
    uniform float uFormSuccess;
    uniform float uRefraction;
    uniform float uInteraction;
    uniform float uContour;
    uniform float uMotion;
    uniform float uHighlight;
    uniform float uTintOpacity;
    uniform float uReducedMotion;
    uniform float uContentVisibility;
    uniform float uSignupVisibility;
    uniform vec3 uBackground;
    uniform vec3 uForeground;
    uniform vec3 uGlassTint;
    uniform sampler2D uPhoto;
    uniform vec2 uPhotoSize;
    uniform float uHasPhoto;

    float hash21(vec2 p) {
      p = fract(p * vec2(123.34, 456.21));
      p += dot(p, p + 45.32);
      return fract(p.x * p.y);
    }

    float valueNoise(vec2 p) {
      vec2 i = floor(p);
      vec2 f = fract(p);
      f = f * f * (3.0 - 2.0 * f);
      float a = hash21(i);
      float b = hash21(i + vec2(1.0, 0.0));
      float c = hash21(i + vec2(0.0, 1.0));
      float d = hash21(i + vec2(1.0, 1.0));
      return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
    }

    float fbm(vec2 p) {
      float value = 0.0;
      float amplitude = 0.52;
      mat2 rotation = mat2(0.80, -0.60, 0.60, 0.80);
      for (int i = 0; i < 4; i++) {
        value += amplitude * valueNoise(p);
        p = rotation * p * 2.03 + 7.1;
        amplitude *= 0.5;
      }
      return value;
    }

    float sdRoundBox(vec2 p, vec2 halfSize, float radius) {
      vec2 q = abs(p) - halfSize + radius;
      return min(max(q.x, q.y), 0.0) + length(max(q, 0.0)) - radius;
    }

    float sdCircle(vec2 p, vec2 center, float radius) {
      return length(p - center) - radius;
    }

    float smoothMinimum(float a, float b, float k) {
      float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
      return mix(b, a, h) - k * h * (1.0 - h);
    }

    float boxFromGeometry(vec2 p, vec4 geometry, float radiusInset) {
      vec2 halfSize = max(geometry.zw - vec2(1.0), vec2(1.0));
      float radius = max(2.0, geometry.w - radiusInset);
      return sdRoundBox(p - geometry.xy, halfSize, radius);
    }

    float fieldDistance(vec2 p) {
      return boxFromGeometry(p, uField, 2.0);
    }

    float actionDistance(vec2 p) {
      return boxFromGeometry(p, uAction, 1.0);
    }

    float formDistance(vec2 p) {
      return boxFromGeometry(p, uForm, 1.0);
    }

    float socialDistance(vec2 p) {
      float distanceField = sdCircle(p, uSocial[0].xy, max(1.0, uSocial[0].z));
      float blendRadius = mix(3.75, 23.75, uSocialOpen);

      for (int i = 1; i < 6; i++) {
        float presence = smoothstep(0.0, 0.8, uSocial[i].w);
        float radius = max(0.8, uSocial[i].z * mix(0.04, 1.0, presence));
        float itemDistance = sdCircle(p, uSocial[i].xy, radius);
        distanceField = smoothMinimum(distanceField, itemDistance, blendRadius * presence + 0.5);
      }

      return distanceField;
    }

    float sceneDistance(vec2 p) {
      return min(formDistance(p), socialDistance(p));
    }

    vec2 sceneNormal(vec2 p) {
      const float epsilon = 1.15;
      float center = sceneDistance(p);
      vec2 gradient = vec2(
        sceneDistance(p + vec2(epsilon, 0.0)) - center,
        sceneDistance(p + vec2(0.0, epsilon)) - center
      );
      return normalize(gradient + vec2(0.00001));
    }

    float rippleWave(vec2 p) {
      float age = uRipple.z;
      float enabled = step(0.0, age) * step(age, 2.6);
      float radius = length(p - uRipple.xy);
      float wave = sin(radius * 0.16 - age * 10.0);
      float ring = exp(-abs(radius - age * 115.0) * 0.024);
      return wave * ring * exp(-age * 1.05) * uRipple.w * enabled;
    }

    vec2 interactiveWarp(vec2 p) {
      vec2 pointerDelta = p - uPointer;
      float pointerDistance = max(length(pointerDelta), 0.001);
      float influence = exp(-pointerDistance * pointerDistance / 62000.0) * uInteraction;
      vec2 tangent = vec2(-pointerDelta.y, pointerDelta.x) / pointerDistance;
      vec2 wake = tangent * dot(uVelocity, tangent) * 17.0 * influence;
      vec2 pressure = normalize(pointerDelta + vec2(0.0001)) * exp(-pointerDistance * 0.014) * 9.0 * influence;
      return wake - pressure;
    }

    vec3 backgroundField(vec2 p) {
      if (uHasPhoto > 0.5) {
        float cover = max(uViewport.x / uPhotoSize.x, uViewport.y / uPhotoSize.y);
        vec2 drawn = uPhotoSize * cover;
        vec2 crop = vec2((drawn.x - uViewport.x) * 0.5, drawn.y - uViewport.y);
        vec2 uv = (vec2(p.x, uViewport.y - p.y) + crop) / drawn;
        return texture(uPhoto, clamp(uv, 0.0, 1.0)).rgb;
      }
      float motionTime = mix(uTime * uMotion, 0.0, uReducedMotion);
      vec2 uv = p / uViewport;
      vec2 centered = (uv - 0.5) * vec2(uViewport.x / max(uViewport.y, 1.0), 1.0);
      vec2 pointerUv = uPointer / uViewport;
      vec2 warped = p + interactiveWarp(p);
      vec2 q = warped / min(uViewport.x, uViewport.y);

      float noiseValue = fbm(q * 3.25 + vec2(motionTime * 0.075, -motionTime * 0.052));
      noiseValue += 0.38 * fbm(q * 6.1 - vec2(motionTime * 0.038, motionTime * 0.044));

      float contourPhase = noiseValue * 8.5 + centered.x * 1.4 - centered.y * 0.7;
      float contourLine = 1.0 - abs(fract(contourPhase) - 0.5) * 2.0;
      contourLine = pow(clamp(contourLine, 0.0, 1.0), 22.0) * uContour;

      float ribbonA = exp(-pow(abs(centered.y - 0.16 * sin(centered.x * 3.7 + motionTime * 0.42) - 0.08), 2.0) * 42.0);
      float ribbonB = exp(-pow(abs(centered.y + 0.21 * sin(centered.x * 2.6 - motionTime * 0.31) + 0.19), 2.0) * 28.0);
      float pointerGlow = exp(-dot(uv - pointerUv, uv - pointerUv) * 8.5) * uInteraction;
      float verticalSheen = 0.5 + 0.5 * sin(centered.x * 5.0 - centered.y * 1.7 + motionTime * 0.24);

      float graphite = 0.018 + contourLine * 0.075 + noiseValue * 0.014;
      vec3 color = mix(uBackground, uForeground, clamp(graphite, 0.0, 0.15));
      color += vec3(1.0) * (ribbonA * 0.075 + ribbonB * 0.045 + pointerGlow * 0.032);
      color -= uForeground * contourLine * 0.025;
      color += (vec3(0.92, 0.94, 0.97) - uBackground) * verticalSheen * 0.025;
      return clamp(color, 0.0, 1.0);
    }

    void main() {
      vec2 p = gl_FragCoord.xy / uDpr;
      vec3 backdrop = backgroundField(p);
      if (uContentVisibility * uSignupVisibility <= 0.0) {
        outColor = vec4(backdrop, 1.0);
        return;
      }
      float formSurfaceDistance = formDistance(p);
      float socialSurfaceDistance = socialDistance(p);
      float distanceField = min(formSurfaceDistance, socialSurfaceDistance);
      float formRegion = step(formSurfaceDistance, socialSurfaceDistance);
      float socialRegion = 1.0 - formRegion;
      float launcherSurfaceDistance = sdCircle(
        p,
        uSocial[0].xy,
        max(1.0, uSocial[0].z - 1.0)
      );
      float launcherUnderlayMask = (
        1.0 - smoothstep(12.0, 30.0, launcherSurfaceDistance)
      ) * socialRegion;
      float inside = 1.0 - smoothstep(-1.6, 1.6, distanceField);
      float outsideShadow = (1.0 - smoothstep(0.0, 24.0, distanceField)) * step(0.0, distanceField);
      outsideShadow *= 1.0 - launcherUnderlayMask;
      vec3 base = backdrop;
      base *= 1.0 - outsideShadow * 0.052;

      if (inside > 0.001) {
        vec2 normal = sceneNormal(p);
        float interiorDepth = clamp(-distanceField / 36.0, 0.0, 1.0);
        float rimDepth = pow(1.0 - interiorDepth, 1.7);
        float wave = rippleWave(p) * uInteraction;
        vec2 velocityShear = uVelocity * 7.0 * uInteraction;

        float actionMask = (1.0 - smoothstep(-4.0, 6.0, actionDistance(p))) * formRegion * (1.0 - uFormSuccess);
        float fieldMask = formRegion * (1.0 - actionMask) * (1.0 - uFormSuccess);
        float lensPolarity = 1.0;
        float refractionPixels = mix(8.0, 29.0, uRefraction) * 0.65;
        vec2 offset = normal * refractionPixels * (0.28 + rimDepth * 0.92) * lensPolarity;
        offset += velocityShear * (0.2 + rimDepth * 0.8);
        offset += normal * wave * 11.0;

        float dispersion = 0.64 + uRefraction * 1.05;
        vec3 refracted;
        refracted.r = backgroundField(p + offset * (1.0 + dispersion * 0.060)).r;
        refracted.g = backgroundField(p + offset).g;
        refracted.b = backgroundField(p + offset * (1.0 - dispersion * 0.055)).b;

        float edge = exp(-abs(distanceField) * 0.65);
        float innerRim = 1.0 - smoothstep(0.0, 5.0, abs(distanceField + 2.5));
        vec2 lightDirection = normalize(vec2(-0.74 + sin(uTime * 0.21) * 0.05, 0.68));
        float facingLight = 0.5 + 0.5 * dot(normal, lightDirection);
        float facingShadow = 0.5 + 0.5 * dot(normal, -lightDirection);
        float brightBevel = edge * pow(facingLight, 1.3);
        float graphiteBevel = edge * pow(facingShadow, 1.15);
        float specular = pow(max(dot(normal, lightDirection), 0.0), 12.0);
        specular += 0.35 * pow(max(dot(normal, -lightDirection), 0.0), 18.0);
        float focusLift = uFormFocus * (1.0 - smoothstep(-8.0, 18.0, formDistance(p)));
        float caustic = pow(max(0.0, sin((p.x + p.y) * 0.026 + uTime * 0.8 + wave)), 18.0) * rimDepth;

        float formVertical = clamp(abs(p.y - uForm.y) / max(uForm.w, 1.0), 0.0, 1.0);
        float meniscusX = uAction.x - uAction.z;
        float meniscusDistance = abs(p.x - meniscusX);
        float meniscusClip = (1.0 - smoothstep(0.58, 0.94, formVertical)) * formRegion * (1.0 - uFormSuccess);
        float meniscusLight = (1.0 - smoothstep(0.25, 0.85, meniscusDistance)) * meniscusClip;
        float meniscusShadow = (1.0 - smoothstep(0.8, 3.2, abs(p.x - meniscusX + 1.65))) * meniscusClip;

        float movingSweep = pow(max(dot(normal, normalize(vec2(-0.42 + 0.13 * sin(uTime * 0.31), 0.91))), 0.0), 26.0);
        movingSweep *= 0.55 + 0.45 * sin(p.y * 0.045 + uTime * 0.72);

        vec3 glass = mix(refracted, uGlassTint, uTintOpacity);
        if (uHasPhoto > 0.5) glass = mix(glass, vec3(0.04), 0.20);
        glass += vec3(1.0) * (brightBevel * 0.30 + innerRim * 0.041 + specular * 0.105 + caustic * 0.045 + movingSweep * 0.0275) * uHighlight;
        glass -= uForeground * (graphiteBevel * 0.12 + interiorDepth * 0.022);
        glass += vec3(1.0) * meniscusLight * 0.15 * uHighlight;
        glass += vec3(0.96, 0.98, 1.0) * focusLift * 0.068;

        float launcherImageMask = (1.0 - smoothstep(
          6.0,
          12.0,
          launcherSurfaceDistance
        )) * socialRegion;
        float glassCoverage = inside * 0.975 * (1.0 - launcherImageMask);
        base = mix(base, glass, glassCoverage);
      }

      outColor = vec4(clamp(mix(backdrop, base, uContentVisibility * uSignupVisibility), 0.0, 1.0), 1.0);
    }
  `;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const parseColor = (value, fallback) => {
    const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(value || '');
    if (!match) return fallback;
    return [
      parseInt(match[1], 16) / 255,
      parseInt(match[2], 16) / 255,
      parseInt(match[3], 16) / 255,
    ];
  };

  class LiquidGlassRenderer {
    constructor(root) {
      this.root = root;
      this.canvas = root.querySelector('[data-liquid-glass]');
      this.formSurface = root.querySelector('[data-glass-form]');
      this.fieldSurface = root.querySelector('.prelaunch__email');
      this.actionSurface = root.querySelector('.prelaunch__submit');
      this.socialOrigin = root.querySelector('[data-glass-social-origin]');
      this.socialPoints = Array.from(root.querySelectorAll('[data-glass-social-point]'));
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedTransparencyQuery = window.matchMedia('(prefers-reduced-transparency: reduce)');

      if (!this.canvas || this.reducedTransparencyQuery.matches) {
        throw new Error('Liquid Glass is disabled by this browser or accessibility preference.');
      }

      this.gl = this.canvas.getContext('webgl2', {
        alpha: false,
        antialias: false,
        depth: false,
        stencil: false,
        desynchronized: true,
        powerPreference: 'high-performance',
        preserveDrawingBuffer: false,
      });

      if (!this.gl) throw new Error('WebGL2 is not available.');

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.program = this.createProgram(VERTEX_SHADER, REAL_FRAGMENT_SHADER);
      this.uniforms = this.collectUniforms([
        'uViewport', 'uDpr', 'uTime', 'uPointer', 'uVelocity', 'uRipple', 'uForm',
        'uField', 'uAction', 'uSocial[0]', 'uSocialOpen', 'uFormFocus',
        'uActionHover', 'uFormSuccess', 'uRefraction',
        'uInteraction', 'uContour', 'uMotion', 'uHighlight', 'uTintOpacity',
        'uReducedMotion', 'uContentVisibility', 'uSignupVisibility', 'uBackground', 'uForeground', 'uGlassTint', 'uPhoto', 'uPhotoSize', 'uHasPhoto',
      ]);
      this.setupGeometry();
      this.photoImage = root.querySelector('[data-glass-background-image]');
      this.photoReady = false;
      this.photoTexture = this.gl.createTexture();
      this.gl.bindTexture(this.gl.TEXTURE_2D, this.photoTexture);
      this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, 1, 1, 0, this.gl.RGBA, this.gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 255]));
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.LINEAR);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
      this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);

      this.background = parseColor(root.dataset.glassBackground, [0.961, 0.961, 0.969]);
      this.foreground = parseColor(root.dataset.glassForeground, [0.192, 0.192, 0.196]);
      this.glassTint = parseColor(root.dataset.glassTint, [0.98, 0.984, 0.992]);
      this.refraction = clamp(Number(root.dataset.refraction || 72) / 100, 0.2, 1);
      this.interaction = clamp(Number(root.dataset.interaction || 55) / 100, 0, 1);
      this.contour = clamp(Number(root.dataset.contour || 48) / 100, 0.1, 1);
      this.motion = clamp(Number(root.dataset.motion || 28) / 100, 0, 1);
      this.highlight = clamp(Number(root.dataset.highlight || 78) / 100, 0.2, 1);
      this.tintOpacity = clamp(Number(root.dataset.tintOpacity || 12) / 100, 0, 0.4);

      this.viewport = { width: 1, height: 1 };
      this.renderScale = 1;
      this.dpr = 1;
      this.pointer = { x: 0, y: 0 };
      this.pointerTarget = { x: 0, y: 0 };
      this.velocity = { x: 0, y: 0 };
      this.velocityTarget = { x: 0, y: 0 };
      this.lastPointer = null;
      this.ripple = { x: 0, y: 0, startedAt: -10, strength: 0 };
      this.socialOpen = 0;
      this.socialOpenTarget = 0;
      this.formFocus = 0;
      this.formFocusTarget = 0;
      this.actionHover = 0;
      this.actionHoverTarget = 0;
      this.formGeometry = new Float32Array([0, 0, 1, 1]);
      this.fieldGeometry = new Float32Array([0, 0, 1, 1]);
      this.actionGeometry = new Float32Array([0, 0, 1, 1]);
      this.socialGeometry = new Float32Array(24);
      this.formSuccess = this.formSurface && this.formSurface.classList.contains('is-success') ? 1 : 0;
      this.geometryDirtyUntil = performance.now() + 1200;
      this.startedAt = performance.now();
      this.lastFrameAt = this.startedAt;
      this.performanceWindow = { elapsed: 0, frames: 0, stableWindows: 0 };
      this.running = true;
      this.entranceMode = ['waiting', 'cracking', 'falling'].includes(this.root.dataset.entryState);
      const inheritedVisibility = Number.parseFloat(this.root.style.getPropertyValue('--entry-content-opacity'));
      this.contentVisibility = Number.isFinite(inheritedVisibility) ? inheritedVisibility : (this.entranceMode ? 0 : 1);
      this.pausedAt = this.entranceMode ? this.startedAt : null;
      this.pausedDuration = 0;
      this.frame = this.frame.bind(this);

      this.bindEvents();
      this.resize();
      this.pointer.x = this.pointerTarget.x = this.viewport.width * 0.5;
      this.pointer.y = this.pointerTarget.y = this.viewport.height * 0.45;
      this.root.classList.remove('is-glass-fallback');
      if (!this.photoImage) this.root.classList.add('is-webgl');
      else {
        this.root.classList.add('is-photo-loading');
        let generation = 0;
        const uploadPhoto = async () => {
          const current = ++generation;
          try {
            await this.photoImage.decode();
            if (this.signal.aborted || current !== generation) return;
            const gl = this.gl;
            gl.bindTexture(gl.TEXTURE_2D, this.photoTexture);
            gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.photoImage);
            // Keep shader aspect ratio tied to the texture actually uploaded.
            this.photoWidth = this.photoImage.naturalWidth;
            this.photoHeight = this.photoImage.naturalHeight;
            this.photoReady = true;
            this.root.classList.remove('is-glass-fallback');
            this.requestDraw();
          } catch (_) {
            if (this.signal.aborted || current !== generation) return;
            this.photoReady = false;
            this.root.classList.remove('is-webgl');
            this.root.classList.add('is-glass-fallback', 'is-photo-loading');
          }
        };
        // Picture sources change on rotation/resizing, including after entrance playback.
        this.photoImage.addEventListener('load', uploadPhoto, { signal: this.signal });
        uploadPhoto();
      }
      this.requestDraw();
    }

    createShader(type, source) {
      const shader = this.gl.createShader(type);
      this.gl.shaderSource(shader, source);
      this.gl.compileShader(shader);
      if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
        const message = this.gl.getShaderInfoLog(shader) || 'Unknown shader compilation error.';
        this.gl.deleteShader(shader);
        throw new Error(message);
      }
      return shader;
    }

    createProgram(vertexSource, fragmentSource) {
      const vertex = this.createShader(this.gl.VERTEX_SHADER, vertexSource);
      const fragment = this.createShader(this.gl.FRAGMENT_SHADER, fragmentSource);
      const program = this.gl.createProgram();
      this.gl.attachShader(program, vertex);
      this.gl.attachShader(program, fragment);
      this.gl.linkProgram(program);
      this.gl.deleteShader(vertex);
      this.gl.deleteShader(fragment);
      if (!this.gl.getProgramParameter(program, this.gl.LINK_STATUS)) {
        const message = this.gl.getProgramInfoLog(program) || 'Unknown shader link error.';
        this.gl.deleteProgram(program);
        throw new Error(message);
      }
      return program;
    }

    collectUniforms(names) {
      return names.reduce((uniforms, name) => {
        uniforms[name] = this.gl.getUniformLocation(this.program, name);
        return uniforms;
      }, {});
    }

    setupGeometry() {
      const vertices = new Float32Array([-1, -1, 3, -1, -1, 3]);
      const buffer = this.gl.createBuffer();
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, buffer);
      this.gl.bufferData(this.gl.ARRAY_BUFFER, vertices, this.gl.STATIC_DRAW);
      const location = this.gl.getAttribLocation(this.program, 'aPosition');
      this.gl.enableVertexAttribArray(location);
      this.gl.vertexAttribPointer(location, 2, this.gl.FLOAT, false, 0, 0);
      this.vertexBuffer = buffer;
    }

    bindEvents() {
      const options = { signal: this.signal, passive: true };

      this.root.addEventListener('pointermove', (event) => {
        const rootRect = this.root.getBoundingClientRect();
        const now = performance.now();
        const x = event.clientX - rootRect.left;
        const y = rootRect.height - (event.clientY - rootRect.top);
        if (this.lastPointer) {
          const delta = Math.max(8, now - this.lastPointer.time);
          this.velocityTarget.x = clamp((x - this.lastPointer.x) / delta, -2.4, 2.4);
          this.velocityTarget.y = clamp((y - this.lastPointer.y) / delta, -2.4, 2.4);
        }
        this.lastPointer = { x, y, time: now };
        this.pointerTarget.x = x;
        this.pointerTarget.y = y;
      }, options);

      this.root.addEventListener('pointerdown', (event) => {
        const rootRect = this.root.getBoundingClientRect();
        this.rippleAt(
          event.clientX - rootRect.left,
          rootRect.height - (event.clientY - rootRect.top),
          event.pointerType === 'touch' ? 1 : 0.72
        );
      }, options);

      if (this.actionSurface) {
        this.actionSurface.addEventListener('pointerenter', () => {
          this.actionHoverTarget = 1;
        }, options);
        this.actionSurface.addEventListener('pointerleave', () => {
          this.actionHoverTarget = 0;
        }, options);
      }

      this.root.addEventListener('focusin', (event) => {
        if (event.target.closest('[data-glass-form]')) this.formFocusTarget = 1;
      }, { signal: this.signal });

      this.root.addEventListener('focusout', (event) => {
        if (event.target.closest('[data-glass-form]')) this.formFocusTarget = 0;
      }, { signal: this.signal });

      document.addEventListener('visibilitychange', () => {
        if (!document.hidden && this.running) {
          this.lastFrameAt = performance.now();
          this.requestDraw();
        }
      }, { signal: this.signal });

      window.addEventListener('resize', () => this.resize(), { signal: this.signal, passive: true });

      this.canvas.addEventListener('webglcontextlost', (event) => {
        event.preventDefault();
        this.running = false;
        cancelAnimationFrame(this.raf);
        this.root.classList.remove('is-webgl');
        this.root.classList.add('is-glass-fallback');
      }, { signal: this.signal });

      this.canvas.addEventListener('webglcontextrestored', () => {
        window.setTimeout(() => window.EDLiquidGlass.mount(this.root, true), 0);
      }, { signal: this.signal });

      this.resizeObserver = new ResizeObserver(() => {
        this.resize();
        this.invalidateGeometry(900);
      });
      this.resizeObserver.observe(this.root);
      if (this.formSurface) this.resizeObserver.observe(this.formSurface);
    }

    resize() {
      const rect = this.root.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      const baseDpr = Math.min(window.devicePixelRatio || 1, 2);
      const dpr = baseDpr * this.renderScale;
      const pixelWidth = Math.max(1, Math.round(width * dpr));
      const pixelHeight = Math.max(1, Math.round(height * dpr));

      this.viewport.width = width;
      this.viewport.height = height;
      this.dpr = dpr;
      if (this.canvas.width !== pixelWidth || this.canvas.height !== pixelHeight) {
        this.canvas.width = pixelWidth;
        this.canvas.height = pixelHeight;
        this.gl.viewport(0, 0, pixelWidth, pixelHeight);
      }
      this.invalidateGeometry(200);
      this.requestDraw();
    }

    requestDraw() {
      if (this.running && !this.raf) this.raf = requestAnimationFrame(this.frame);
    }

    setEntranceMode(active) {
      if (this.entranceMode === active) return;
      this.entranceMode = active;
      if (active) {
        this.pausedAt = performance.now();
      } else if (this.pausedAt !== null) {
        this.pausedDuration += performance.now() - this.pausedAt;
        this.pausedAt = null;
      }
      this.lastFrameAt = performance.now();
      this.requestDraw();
    }

    setContentVisibility(value) {
      this.contentVisibility = clamp(value, 0, 1);
      this.requestDraw();
    }

    invalidateGeometry(duration = 650) {
      this.geometryDirtyUntil = Math.max(this.geometryDirtyUntil, performance.now() + duration);
    }

    rectToShape(element, fallbackRadius = 1) {
      if (!element) return [0, 0, fallbackRadius, 0];
      const rootRect = this.root.getBoundingClientRect();
      const rect = element.getBoundingClientRect();
      const style = window.getComputedStyle(element);
      return [
        rect.left - rootRect.left + rect.width * 0.5,
        rootRect.height - (rect.top - rootRect.top + rect.height * 0.5),
        Math.max(fallbackRadius, Math.min(rect.width, rect.height) * 0.5),
        Number.parseFloat(style.opacity) || 0,
      ];
    }

    writeBoxGeometry(target, element, fallback, overlap = 0) {
      if (!element) {
        target.set(fallback);
        return;
      }
      const rootRect = this.root.getBoundingClientRect();
      const rect = element.getBoundingClientRect();
      target[0] = rect.left - rootRect.left + rect.width * 0.5;
      target[1] = rootRect.height - (rect.top - rootRect.top + rect.height * 0.5);
      target[2] = Math.max(1, rect.width * 0.5 + overlap);
      target[3] = Math.max(1, rect.height * 0.5);
    }

    syncGeometry() {
      const rootRect = this.root.getBoundingClientRect();
      if (this.formSurface) {
        const rect = this.formSurface.getBoundingClientRect();
        this.formGeometry[0] = rect.left - rootRect.left + rect.width * 0.5;
        this.formGeometry[1] = rootRect.height - (rect.top - rootRect.top + rect.height * 0.5);
        this.formGeometry[2] = Math.max(1, rect.width * 0.5);
        this.formGeometry[3] = Math.max(1, rect.height * 0.5);
      }

      this.writeBoxGeometry(this.fieldGeometry, this.fieldSurface, this.formGeometry, 4);
      this.writeBoxGeometry(this.actionGeometry, this.actionSurface, this.formGeometry, 4);

      const origin = this.rectToShape(this.socialOrigin, 28);
      this.socialGeometry.set(origin, 0);
      for (let index = 0; index < 5; index++) {
        const shape = this.rectToShape(this.socialPoints[index], 1);
        this.socialGeometry.set(shape, (index + 1) * 4);
      }
    }

    rippleAt(x, y, strength = 1) {
      this.ripple.x = x;
      this.ripple.y = y;
      this.ripple.startedAt = performance.now();
      this.ripple.strength = strength;
    }

    rippleFrom(element, strength = 1) {
      if (!element) return;
      const rootRect = this.root.getBoundingClientRect();
      const rect = element.getBoundingClientRect();
      this.rippleAt(
        rect.left - rootRect.left + rect.width * 0.5,
        rootRect.height - (rect.top - rootRect.top + rect.height * 0.5),
        strength
      );
    }

    setSocialOpen(open) {
      this.socialOpenTarget = open ? 1 : 0;
      this.invalidateGeometry(950);
      this.rippleFrom(this.socialOrigin, open ? 1 : 0.58);
    }

    markSubmitting(element) {
      this.rippleFrom(element || this.formSurface, 1.15);
      this.formFocusTarget = 1;
    }

    adaptResolution(frameDuration) {
      if (frameDuration <= 0 || frameDuration > 250) return;
      this.performanceWindow.elapsed += frameDuration;
      this.performanceWindow.frames += 1;
      if (this.performanceWindow.frames < 120) return;

      const average = this.performanceWindow.elapsed / this.performanceWindow.frames;
      if (average > 22.2 && this.renderScale > 0.8) {
        this.renderScale = 0.8;
        this.performanceWindow.stableWindows = 0;
        this.resize();
      } else if (average < 15.8 && this.renderScale < 1) {
        this.performanceWindow.stableWindows += 1;
        if (this.performanceWindow.stableWindows >= 2) {
          this.renderScale = 1;
          this.performanceWindow.stableWindows = 0;
          this.resize();
        }
      } else {
        this.performanceWindow.stableWindows = 0;
      }

      this.performanceWindow.elapsed = 0;
      this.performanceWindow.frames = 0;
    }

    frame(now) {
      this.raf = null;
      if (!this.running) return;
      if (document.hidden) return;

      const frameDuration = now - this.lastFrameAt;
      this.lastFrameAt = now;
      this.adaptResolution(frameDuration);

      this.pointer.x += (this.pointerTarget.x - this.pointer.x) * 0.12;
      this.pointer.y += (this.pointerTarget.y - this.pointer.y) * 0.12;
      this.velocity.x += (this.velocityTarget.x - this.velocity.x) * 0.13;
      this.velocity.y += (this.velocityTarget.y - this.velocity.y) * 0.13;
      this.velocityTarget.x *= 0.9;
      this.velocityTarget.y *= 0.9;
      this.socialOpen += (this.socialOpenTarget - this.socialOpen) * 0.105;
      this.formFocus += (this.formFocusTarget - this.formFocus) * 0.12;
      this.actionHover += (this.actionHoverTarget - this.actionHover) * 0.14;

      if (now < this.geometryDirtyUntil || this.performanceWindow.frames % 45 === 0) {
        this.syncGeometry();
      }

      const gl = this.gl;
      gl.useProgram(this.program);
      if (this.photoImage && !this.photoReady) return;
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.photoTexture);
      gl.uniform1i(this.uniforms.uPhoto, 0);
      gl.uniform1f(this.uniforms.uHasPhoto, this.photoReady ? 1 : 0);
      gl.uniform2f(this.uniforms.uPhotoSize, this.photoWidth || 1, this.photoHeight || 1);
      gl.uniform2f(this.uniforms.uViewport, this.viewport.width, this.viewport.height);
      gl.uniform1f(this.uniforms.uDpr, this.dpr);
      gl.uniform1f(this.uniforms.uTime, ((this.pausedAt ?? now) - this.startedAt - this.pausedDuration) * 0.001);
      gl.uniform2f(this.uniforms.uPointer, this.pointer.x, this.pointer.y);
      gl.uniform2f(this.uniforms.uVelocity, this.velocity.x, this.velocity.y);
      gl.uniform4f(
        this.uniforms.uRipple,
        this.ripple.x,
        this.ripple.y,
        (now - this.ripple.startedAt) * 0.001,
        this.ripple.strength
      );
      gl.uniform4fv(this.uniforms.uForm, this.formGeometry);
      gl.uniform4fv(this.uniforms.uField, this.fieldGeometry);
      gl.uniform4fv(this.uniforms.uAction, this.actionGeometry);
      gl.uniform4fv(this.uniforms['uSocial[0]'], this.socialGeometry);
      gl.uniform1f(this.uniforms.uSocialOpen, this.socialOpen);
      gl.uniform1f(this.uniforms.uFormFocus, this.formFocus);
      gl.uniform1f(this.uniforms.uActionHover, this.actionHover);
      gl.uniform1f(this.uniforms.uFormSuccess, this.formSuccess);
      gl.uniform1f(this.uniforms.uRefraction, this.refraction);
      gl.uniform1f(this.uniforms.uInteraction, this.interaction);
      gl.uniform1f(this.uniforms.uContour, this.contour);
      gl.uniform1f(this.uniforms.uMotion, this.motion);
      gl.uniform1f(this.uniforms.uHighlight, this.highlight);
      gl.uniform1f(this.uniforms.uTintOpacity, this.tintOpacity);
      gl.uniform1f(this.uniforms.uReducedMotion, this.reducedMotionQuery.matches ? 1 : 0);
      gl.uniform1f(this.uniforms.uContentVisibility, this.contentVisibility);
      const signupVisibility = Number.parseFloat(this.root.style.getPropertyValue('--desktop-signup-opacity'));
      gl.uniform1f(this.uniforms.uSignupVisibility, Number.isFinite(signupVisibility) ? signupVisibility : 1);
      gl.uniform3fv(this.uniforms.uBackground, this.background);
      gl.uniform3fv(this.uniforms.uForeground, this.foreground);
      gl.uniform3fv(this.uniforms.uGlassTint, this.glassTint);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      this.root.classList.remove('is-photo-loading');
      this.root.classList.add('is-webgl');
      if (!this.entranceMode) this.requestDraw();
    }

    destroy() {
      this.running = false;
      cancelAnimationFrame(this.raf);
      this.abortController.abort();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.gl && this.program) this.gl.deleteProgram(this.program);
      if (this.gl && this.vertexBuffer) this.gl.deleteBuffer(this.vertexBuffer);
      if (this.gl && this.photoTexture) this.gl.deleteTexture(this.photoTexture);
      this.root.classList.remove('is-webgl');
    }
  }

  const instances = new WeakMap();

  const mount = (root, force = false) => {
    if (!root) return null;
    const existing = instances.get(root);
    if (existing && !force) return existing;
    if (existing) existing.destroy();

    try {
      const renderer = new LiquidGlassRenderer(root);
      instances.set(root, renderer);
      return renderer;
    } catch (error) {
      root.classList.remove('is-webgl');
      root.classList.add('is-glass-fallback');
      if (window.Shopify && window.Shopify.designMode) {
        console.warn('[Eastwood Danso Liquid Glass]', error.message);
      }
      return null;
    }
  };

  window.EDLiquidGlass = {
    mount,
    get: (root) => instances.get(root) || null,
  };
})();
